import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Pagination } from "@/components/common/Pagination";
import { useToast } from "@/components/common";
import { useAlert } from "@/hooks/useAlert";
import { UploadDocumentsModal } from "@/features/admin/contacts/components/UploadDocumentsModal";
import { documentsService, } from "@/api/services/documents.service";
import { formatDateOnlyAUS } from "@/utils/dateTime";
import styles from "./SiteDocumentsPhotosTab.module.css";
const FILTER_TABS = [
    { id: "all", label: "All" },
    { id: "plans", label: "Plans" },
    { id: "images", label: "Images" },
    { id: "other", label: "Other" },
];
const UPLOAD_CATEGORIES = ["Plans", "Images", "Other"];
export const SiteDocumentsPhotosTab = ({ siteId: siteIdProp, } = {}) => {
    const params = useParams();
    const effectiveSiteId = siteIdProp ?? params.siteId ?? null;
    const { error: showError } = useToast();
    const { confirm } = useAlert();
    const [documents, setDocuments] = useState([]);
    const [counts, setCounts] = useState({
        all: 0,
        plans: 0,
        images: 0,
        other: 0,
        permits: 0,
        invoices: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortField, setSortField] = useState("date");
    const [sortDirection, setSortDirection] = useState("desc");
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const pageSize = 10;
    const fetchDocuments = useCallback(async () => {
        if (!effectiveSiteId)
            return;
        try {
            setLoading(true);
            setError(null);
            // Fetch documents and counts in parallel
            const [docsResponse, countsResponse] = await Promise.all([
                documentsService.getDocumentsBySite(effectiveSiteId, activeFilter !== "all" ? activeFilter : undefined),
                documentsService.getDocumentCountsBySite(effectiveSiteId),
            ]);
            let filteredDocs = docsResponse.data;
            // Apply search filter
            if (searchTerm) {
                const search = searchTerm.toLowerCase();
                filteredDocs = filteredDocs.filter((doc) => doc.name.toLowerCase().includes(search) ||
                    doc.fileName.toLowerCase().includes(search) ||
                    doc.uploadedByName?.toLowerCase().includes(search));
            }
            // Apply sorting
            filteredDocs.sort((a, b) => {
                if (sortField === "name") {
                    return sortDirection === "asc"
                        ? a.name.localeCompare(b.name)
                        : b.name.localeCompare(a.name);
                }
                else {
                    const dateA = new Date(a.createdAt).getTime();
                    const dateB = new Date(b.createdAt).getTime();
                    return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
                }
            });
            // Calculate pagination
            const totalItems = filteredDocs.length;
            const calculatedTotalPages = Math.ceil(totalItems / pageSize) || 1;
            setTotalPages(calculatedTotalPages);
            // Apply pagination
            const startIndex = (currentPage - 1) * pageSize;
            const paginatedDocs = filteredDocs.slice(startIndex, startIndex + pageSize);
            setDocuments(paginatedDocs);
            setCounts(countsResponse.data);
        }
        catch (err) {
            console.error("Error fetching documents:", err);
            setError("Failed to load documents");
        }
        finally {
            setLoading(false);
        }
    }, [
        effectiveSiteId,
        activeFilter,
        searchTerm,
        sortField,
        sortDirection,
        currentPage,
    ]);
    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);
    // Reset to page 1 when filter or search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [activeFilter, searchTerm]);
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        }
        else {
            setSortField(field);
            setSortDirection("desc");
        }
    };
    const handleDownload = async (documentId) => {
        try {
            const response = await documentsService.getDownloadUrl(documentId);
            window.open(response.data.url, "_blank");
        }
        catch (err) {
            console.error("Error downloading document:", err);
            showError("Failed to download document");
        }
    };
    const handleDelete = async (documentId) => {
        const confirmed = await confirm("Are you sure you want to delete this document?", "Confirm Deletion");
        if (!confirmed)
            return;
        try {
            setDeletingId(documentId);
            await documentsService.deleteDocument(documentId);
            fetchDocuments();
        }
        catch (err) {
            console.error("Error deleting document:", err);
            showError("Failed to delete document");
        }
        finally {
            setDeletingId(null);
        }
    };
    const handleUploadClick = () => {
        setIsUploadModalOpen(true);
    };
    const handleSaveDocuments = async (files, selectedSiteCategory) => {
        if (!effectiveSiteId)
            return;
        setIsUploading(true);
        try {
            // Map site category from modal format to API format
            const siteCategoryMap = {
                Plans: "plans",
                Images: "images",
                Other: "other",
            };
            const siteCategory = siteCategoryMap[selectedSiteCategory] || "other";
            // Upload each file sequentially
            for (const fileInfo of files) {
                if (!fileInfo.file) {
                    console.error("File object missing for:", fileInfo.name);
                    throw new Error(`File object missing for ${fileInfo.name}`);
                }
                await documentsService.uploadDocument(fileInfo.file, {
                    siteId: effectiveSiteId,
                    siteCategory,
                    name: fileInfo.name.split(".")[0],
                });
            }
            setIsUploadModalOpen(false);
            await fetchDocuments();
        }
        catch (err) {
            console.error("Error uploading documents:", err);
            const errorMessage = err?.response?.data?.message ||
                err?.message ||
                "Failed to upload documents";
            showError(`Failed to upload documents: ${errorMessage}`);
        }
        finally {
            setIsUploading(false);
        }
    };
    const formatDate = (dateString) => formatDateOnlyAUS(dateString);
    const getFileType = (mimeType) => {
        const typeMap = {
            "application/pdf": "PDF",
            "image/jpeg": "JPEG",
            "image/png": "PNG",
            "image/gif": "GIF",
            "image/webp": "WEBP",
            "application/msword": "DOC",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
            "application/vnd.ms-excel": "XLS",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
        };
        return (typeMap[mimeType] || mimeType.split("/").pop()?.toUpperCase() || "FILE");
    };
    if (!effectiveSiteId) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "Link a site to this estimate to view and upload documents." }) }));
    }
    if (loading && documents.length === 0) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "Loading documents..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchDocuments, className: styles.retryButton, children: "Retry" })] }) }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.filterTabs, children: [_jsx("div", { children: FILTER_TABS.map((tab) => (_jsx("button", { className: `${styles.filterTab} ${activeFilter === tab.id ? styles.active : ""}`, onClick: () => setActiveFilter(tab.id), children: tab.label }, tab.id))) }), _jsxs("div", { className: styles.header, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsxs("svg", { className: styles.searchIcon, width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsx("button", { className: styles.addButton, onClick: handleUploadClick, children: "Add Documents" })] })] }), _jsx("div", { className: styles.tableWrapper, children: _jsxs("table", { className: styles.table, children: [_jsx("thead", { children: _jsxs("tr", { className: styles.tableHeader, children: [_jsxs("th", { className: styles.sortableHeader, onClick: () => handleSort("name"), children: [_jsx("span", { children: "NAME" }), sortField === "name" && (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", style: {
                                                    transform: sortDirection === "asc" ? "rotate(180deg)" : "none",
                                                }, children: _jsx("polyline", { points: "6 9 12 15 18 9" }) }))] }), _jsxs("th", { className: styles.sortableHeader, onClick: () => handleSort("date"), children: [_jsx("span", { children: "DATE" }), sortField === "date" && (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", style: {
                                                    transform: sortDirection === "asc" ? "rotate(180deg)" : "none",
                                                }, children: _jsx("polyline", { points: "6 9 12 15 18 9" }) }))] }), _jsx("th", { children: "UPLOADED BY" })] }) }), _jsx("tbody", { children: documents.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 3, className: styles.emptyState, children: _jsxs("div", { className: styles.emptyStateContent, children: [_jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("polyline", { points: "14 2 14 8 20 8" })] }), _jsx("p", { children: "No documents found" })] }) }) })) : (documents.map((doc) => (_jsxs("tr", { className: styles.tableRow, children: [_jsx("td", { children: _jsx("div", { className: styles.documentName, children: _jsxs("div", { className: styles.documentInfo, children: [_jsx("span", { className: styles.name, children: doc.name }), _jsxs("span", { className: styles.meta, children: [doc.fileSizeFormatted, " \u2022 ", getFileType(doc.mimeType)] })] }) }) }), _jsx("td", { children: _jsx("span", { className: styles.date, children: formatDate(doc.createdAt) }) }), _jsx("td", { children: _jsxs("div", { className: styles.uploadedBy, children: [_jsxs("div", { className: styles.userInfo, children: [_jsx("div", { className: styles.avatarPlaceholder, children: (doc.uploadedByName || "U").charAt(0) }), _jsx("span", { className: styles.userName, children: doc.uploadedByName || "Unknown" })] }), _jsxs("div", { className: styles.actions, children: [_jsx("button", { className: styles.actionButton, onClick: () => handleDownload(doc.id), title: "Download", children: _jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "7 10 12 15 17 10" }), _jsx("line", { x1: "12", y1: "15", x2: "12", y2: "3" })] }) }), _jsx("button", { className: styles.deleteButton, onClick: () => handleDelete(doc.id), title: "Delete", disabled: deletingId === doc.id, children: deletingId === doc.id ? (_jsx("span", { className: styles.spinner })) : (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor", stroke: "none", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("line", { x1: "8", y1: "12", x2: "16", y2: "12", stroke: "white", strokeWidth: "2" })] })) })] })] }) })] }, doc.id)))) })] }) }), counts.all > 0 && (_jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, pageSize: pageSize, onPageChange: setCurrentPage })), _jsx(UploadDocumentsModal, { isOpen: isUploadModalOpen, onClose: () => setIsUploadModalOpen(false), onSave: handleSaveDocuments, categories: UPLOAD_CATEGORIES, categoryLabel: "SITE CATEGORY", isUploading: isUploading })] }));
};
